/**
 * Панель «Опубликованные PR» админ-ревью обновлений.
 *
 * Read-only список PR, которые уже вышли в опубликованных карточках «Что нового».
 * Повторно анонсировать их нельзя — панель нужна для справки (что уже показано
 * пользователям и в какой карточке). Свёрнута по умолчанию, без чекбоксов и
 * генерации — в отличие от `PrCandidatesPanel`.
 *
 * FSD: feature — импортирует из entities/shared, наружу отдаёт только UI.
 */

import { useState } from 'react';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { IconChevronDown, IconCheck } from '@shared/ui/icons';
import { useGetPublishedPrsQuery } from '@shared/api';

/** Дата + часы:минуты (ru-RU). `null`/невалидное — прочерк. */
function formatDtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ── layout ──────────────────────────────────────────────────────────────── */

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(4)};
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset ${({ theme }) => theme.shadow.focus};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const HeaderCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Chevron = styled(IconChevronDown)<{ $open: boolean }>`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.text.muted};
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Body = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Lead = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled.li`
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
`;

const RowTitle = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const PrNum = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-right: ${({ theme }) => theme.spacing(2)};
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Muted = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const PublishedIn = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};

  & b {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const StateNote = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing(6)};
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/* ── panel ───────────────────────────────────────────────────────────────── */

export function PublishedPrsPanel() {
  const [open, setOpen] = useState(false);
  const { data: items = [], isFetching, isError } = useGetPublishedPrsQuery();

  const bodyId = 'published-prs-body';

  return (
    <Section>
      <HeaderButton
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <IconCheck size={16} aria-hidden />
        <HeaderTitle>Опубликованные PR</HeaderTitle>
        <HeaderCount>({items.length})</HeaderCount>
        <Chevron $open={open} size={18} aria-hidden />
      </HeaderButton>

      {open && (
        <Body id={bodyId}>
          <Lead>
            PR, уже вышедшие в опубликованных карточках «Что нового». Повторно анонсировать их
            нельзя — список нужен для справки.
          </Lead>

          {isFetching && items.length === 0 ? (
            <StateNote>Загрузка…</StateNote>
          ) : isError ? (
            <StateNote>Не удалось загрузить список.</StateNote>
          ) : items.length === 0 ? (
            <StateNote>Пока ничего не опубликовано.</StateNote>
          ) : (
            <List>
              {items.map((pr) => (
                <Row key={pr.number}>
                  <RowTitle>
                    <PrNum>#{pr.number}</PrNum>
                    {pr.title}
                  </RowTitle>
                  <RowMeta>
                    {pr.author && <Badge $tone="neutral">автор: {pr.author}</Badge>}
                    <Muted>Смерджен: {formatDtTime(pr.merged_at)}</Muted>
                  </RowMeta>
                  <PublishedIn>
                    Опубликовано в «<b>{pr.announcement_title}</b>» —{' '}
                    {formatDtTime(pr.published_at)}
                  </PublishedIn>
                </Row>
              ))}
            </List>
          )}
        </Body>
      )}
    </Section>
  );
}
